// Patient-facing AI assistant — the sidebar's "AI Chat" tab.
//
// Visually a port of `pages/medstudent/MedStudentAIAssistant.js` (same header,
// source chips, bubbles, suggested prompts, composer) so the patient portal and
// the clinic page read as one product. Three deliberate differences:
//
//   1. It calls `/api/patient/ai-assistant/query/`, which runs over the
//      patient-safe AI SUMMARIES rather than the raw note and raw transcripts,
//      and uses a patient-facing system prompt.
//   2. No threads / pinned prompts / settings gear. Those are clinician
//      features backed by `ai_feedback` + the thread endpoints, and the
//      settings modal is password-gated on SETTINGS_PASSWORD — none of which
//      belongs in a patient's hands. History is per-session, in component state.
//   3. Source labels are written for the patient ("Visit Summary", not
//      "Med Docs"), though the wire keys stay the clinic ones so the two
//      components remain diffable.
//
// Tailwind here is inert unless an ancestor carries id="clinic-root"
// (tailwind.config.js `important: '#clinic-root'`). PatientRightPanel provides
// that wrapper — see the note there before reusing this component elsewhere.
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { queryPatientAssistant } from "./patientPortalApi";

// Wire keys match the backend's VALID_SOURCES (api/patient_ai.py). The labels
// are the patient-facing rename; `medical_document` and `audio_transcripts`
// resolve to SUMMARIES on the backend despite the clinic-inherited names.
export const PATIENT_SOURCES = [
  { key: "patient_info", label: "My Info", defaultOn: true },
  { key: "medical_document", label: "Visit Summary", defaultOn: true },
  { key: "audio_transcripts", label: "Recording Summaries", defaultOn: true },
  { key: "uploaded_files", label: "My Reports", defaultOn: true },
  { key: "ai_knowledge", label: "AI Knowledge", defaultOn: true },
  { key: "visit_history", label: "Visit History", defaultOn: false },
  { key: "forms", label: "Forms", defaultOn: false },
];

const SUGGESTED_PROMPTS = [
  "What happened at my visit?",
  "What are my next steps?",
  "Explain this in simpler terms",
  "What did we talk about?",
  "When is my next visit?",
];

function nowHHmm() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function defaultGreeting(firstName) {
  const who = firstName ? `, ${firstName}` : "";
  return (
    `Hi${who}! I can help you understand your visit. I can see the plain-language ` +
    `summaries of your visits and recordings, your uploaded reports, and your visit ` +
    `history — not the full clinical note. Ask me anything, and for medical advice ` +
    `always check with your care team.`
  );
}

// Markdown renderers copied from the clinic assistant so answers render
// identically in both surfaces (tables included — see clinic.css .ai-markdown).
const MARKDOWN_COMPONENTS = {
  p: (props) => (
    <p className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-2" {...props} />
  ),
  strong: (props) => (
    <strong className="font-semibold text-slate-900 dark:text-slate-100" {...props} />
  ),
  ul: (props) => (
    <ul className="list-disc pl-5 space-y-1.5 my-2 text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />
  ),
  ol: (props) => (
    <ol className="list-decimal pl-5 space-y-1.5 my-2 text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />
  ),
  li: (props) => (
    <li className="text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />
  ),
  h1: (props) => (
    <h1 className="text-sm md:text-base font-bold text-slate-900 dark:text-slate-100 mt-3 mb-1.5" {...props} />
  ),
  h2: (props) => (
    <h2 className="text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 mt-2.5 mb-1" {...props} />
  ),
  h3: (props) => (
    <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-200 mt-2 mb-1" {...props} />
  ),
  table: (props) => (
    <div className="overflow-x-auto w-full my-3 rounded-xl border border-slate-250 dark:border-slate-800/80 bg-white dark:bg-[#0c0c12] shadow-sm">
      <table className="min-w-full border-collapse text-xs md:text-sm text-slate-700 dark:text-slate-300" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-slate-50 dark:bg-slate-900/60" {...props} />,
  th: (props) => (
    <th className="border-b border-slate-250 dark:border-slate-800 px-3 py-2 font-bold text-left text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900/40" {...props} />
  ),
  td: (props) => (
    <td className="border-b border-slate-250 dark:border-slate-800 px-3 py-2 text-slate-700 dark:text-slate-300" {...props} />
  ),
};

export default function PatientAIAssistant({
  appointmentId,
  patientFirstName,
  patientName,
  userInitials,
}) {
  const [selectedSources, setSelectedSources] = useState(() =>
    Object.fromEntries(PATIENT_SOURCES.map((s) => [s.key, s.defaultOn]))
  );
  const [messages, setMessages] = useState(() => [
    { role: "assistant", text: defaultGreeting(patientFirstName), time: nowHHmm() },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Follow the conversation as it grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(
    async (text) => {
      const q = (text || "").trim();
      if (!q || loading) return;
      // Snapshot the history BEFORE appending, so the backend gets prior turns
      // and not a copy of the question it is already being asked.
      const history = messages
        .filter((m) => m.text)
        .map((m) => ({ role: m.role, text: m.text }));

      setMessages((m) => [...m, { role: "user", text: q, time: nowHHmm() }]);
      setInput("");
      setLoading(true);
      try {
        const data = await queryPatientAssistant({
          query: q,
          appointmentId,
          selectedSources: Object.entries(selectedSources)
            .filter(([, on]) => on)
            .map(([k]) => k),
          history,
        });
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            text: data.answer || "I couldn't come up with an answer for that.",
            sources: Array.isArray(data.sources) ? data.sources : [],
            time: nowHHmm(),
          },
        ]);
      } catch (err) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            text: err.message || "Something went wrong. Please try again.",
            error: true,
            time: nowHHmm(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [appointmentId, loading, messages, selectedSources]
  );

  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-[#0a0a0f]" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800/60">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 flex items-center justify-center text-white shrink-0 shadow-md shadow-blue-500/30">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm6 10l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3zM5 14l.8 2.4L8 17l-2.2.6L5 20l-.8-2.4L2 17l2.2-.6L5 14z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-bold text-slate-900 dark:text-slate-100">AI Assistant</div>
              <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                LIVE
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Answering from your own records
              {patientName ? (
                <>
                  {" · "}
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {patientName}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Source chips — same control as the clinic assistant, patient labels. */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {PATIENT_SOURCES.map((src) => {
            const on = !!selectedSources[src.key];
            return (
              <button
                key={src.key}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setSelectedSources((s) => ({ ...s, [src.key]: !s[src.key] }))
                }
                className={[
                  "text-[11px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5",
                  on
                    ? "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300"
                    : "bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400",
                ].join(" ")}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${on ? "bg-blue-500" : "bg-slate-400"}`}
                />
                {src.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-5">
        {messages.map((m, idx) => {
          if (m.role === "assistant") {
            return (
              <div key={idx}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white text-[10px]">
                    ✦
                  </div>
                  <div className="text-[10px] font-bold tracking-widest text-slate-500 dark:text-slate-400">
                    ASSISTANT
                  </div>
                </div>
                <div
                  className={[
                    "rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed ai-markdown overflow-x-auto",
                    m.error
                      ? "bg-red-50 dark:bg-red-500/[0.08] text-red-700 dark:text-red-300"
                      : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200",
                  ].join(" ")}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                    {m.text || ""}
                  </ReactMarkdown>
                </div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-2">
                  {m.time && <span>{m.time}</span>}
                  {m.sources && m.sources.length > 0 && (
                    <span className="text-slate-500 dark:text-slate-400">
                      · based on: {m.sources.join(", ")}
                    </span>
                  )}
                </div>
              </div>
            );
          }
          return (
            <div key={idx}>
              <div className="flex items-center gap-2 mb-1.5 justify-end">
                <div className="text-[10px] font-bold tracking-widest text-slate-500 dark:text-slate-400">
                  YOU
                </div>
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">
                  {(userInitials || "U").slice(0, 2).toUpperCase()}
                </div>
              </div>
              <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed ml-auto max-w-[85%] whitespace-pre-wrap">
                {m.text}
              </div>
              {m.time && (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 text-right">
                  {m.time}
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
            Assistant is thinking…
          </div>
        )}
      </div>

      {/* Suggested chips + composer */}
      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800/60">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SUGGESTED_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => send(p)}
              disabled={loading}
              className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700/60 disabled:opacity-50"
            >
              {p}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
          className="relative flex items-center bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/60 rounded-xl pr-2 focus-within:border-blue-400 dark:focus-within:border-blue-500"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your visit, your reports, or your next steps…"
            aria-label="Ask the assistant"
            className="flex-1 bg-transparent px-4 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            aria-label="Send"
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-600 text-white disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </form>
        <div className="mt-2 text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
          This assistant reads the plain-language summaries of your care, not your
          full chart. It can't give medical advice — message your care team for that.
        </div>
      </div>
    </div>
  );
}
